import httpClient from '@/lib/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuthStore from '@/store/useAuthStore'

import { CONNECTION_KEYS } from '@/services/api/integrations/useAccountingConnection'
import type { AccountingProvider } from '@/services/api/integrations/accountingProviders'

// The Space's accounting connection, whichever provider it is. (Named for Xero,
// the first provider; the API serves these endpoints for Zoho Books too.)
const BASE = 'team/integrations/accounting'

export type Direction = 'in' | 'out'

export type AccountClass =
  | 'bank'
  | 'income'
  | 'expense'
  | 'liability'
  | 'asset'
  | 'equity'
  | 'other'

export interface VaultMapping {
  vault_id: number
  is_default: boolean
  vault?: { id: number; name: string }
}

export interface CategoryMapping {
  id: number
  direction: Direction
  pf_category_id: number
  effective_from: string
  category?: { id: number; name: string }
}

export interface ContactCategoryMapping extends CategoryMapping {
  external_contact_id: number
}

export interface XeroAccount {
  id: number
  name: string
  code: string | null
  account_class: AccountClass
  currency_code: string | null
  is_active: boolean
  has_transactions: boolean
  vault_mappings: VaultMapping[]
  category_mappings: CategoryMapping[]
  contact_category_mappings: ContactCategoryMapping[]
}

export interface XeroContact {
  id: number
  name: string
  contact_type: string
  email: string | null
}

export interface XeroMappingOverview {
  connection: {
    id: number
    provider: AccountingProvider
    tenant_name: string | null
    status: string
  } | null
  references_refreshing?: boolean
  accounts: XeroAccount[]
  contacts: XeroContact[]
  can: { map_vaults: boolean; map_categories: boolean; manage: boolean }
}

/**
 * A first mapping applies to every transaction (imported history included);
 * later changes are effective-dated from the date the user picks.
 */
export const ALL_DATES = '2000-01-01'

const KEY = 'xero-mapping'

export function useXeroMapping({ enabled = true }: { enabled?: boolean } = {}) {
  const teamId = useAuthStore((state) => state.currentTeamId)

  return useQuery<XeroMappingOverview>({
    queryKey: [KEY, teamId],
    queryFn: async () => (await httpClient.get(`${BASE}/mappings`)).data,
    enabled: enabled && !!teamId,
    // While the provider's accounts are being re-imported, poll until they appear.
    refetchInterval: (query) =>
      query.state.data?.references_refreshing ? 5000 : false,
  })
}

function useMappingMutation<T>(
  request: (vars: T) => Promise<unknown>,
  success?: string,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] })
      // Integrations card counts (unmapped accounts) depend on mappings too.
      Object.values(CONNECTION_KEYS).forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      )
      if (success) toast.success(success)
    },
    onError: (error: any) => {
      const errors = error?.response?.data?.errors
      const first = errors ? (Object.values(errors)[0] as string[])[0] : null
      toast.error(
        first ??
          error?.response?.data?.message ??
          'Could not save the mapping.',
      )
    },
  })
}

export function useSaveVaultMapping() {
  return useMappingMutation(
    ({
      accountId,
      vaultIds,
      defaultVaultId,
      rawTransactionId,
    }: {
      accountId: number
      vaultIds: number[]
      defaultVaultId: number | null
      rawTransactionId?: number
    }) =>
      httpClient.put(`${BASE}/accounts/${accountId}/vaults`, {
        vault_ids: vaultIds,
        default_vault_id: defaultVaultId,
        raw_transaction_id: rawTransactionId,
      }),
    'Vault mapping saved',
  )
}

export function useSaveCategoryMapping() {
  return useMappingMutation(
    ({
      accountId,
      contactId,
      direction,
      categoryId,
      effectiveFrom,
      rawTransactionId,
    }: {
      accountId: number
      contactId?: number
      direction: Direction
      categoryId: number
      effectiveFrom: string
      rawTransactionId?: number
    }) =>
      httpClient.post(
        contactId
          ? `${BASE}/accounts/${accountId}/contacts/${contactId}/categories`
          : `${BASE}/accounts/${accountId}/categories`,
        {
          direction,
          pf_category_id: categoryId,
          effective_from: effectiveFrom,
          raw_transaction_id: rawTransactionId,
        },
      ),
    'Category mapping saved',
  )
}

export function useRemoveContactOverride() {
  return useMappingMutation(
    ({
      accountId,
      contactId,
      direction,
    }: {
      accountId: number
      contactId: number
      direction: Direction
    }) =>
      httpClient.delete(
        `${BASE}/accounts/${accountId}/contacts/${contactId}/categories`,
        { data: { direction } },
      ),
    'Contact override removed',
  )
}

export function useRefreshXeroReferences() {
  return useMappingMutation(
    () => httpClient.post(`${BASE}/refresh`),
    'Refreshing accounts and contacts…',
  )
}

export function useRecategorisePending() {
  return useMappingMutation(
    () => httpClient.post(`${BASE}/re-categorise-pending`),
    'Pending transactions are being re-categorised',
  )
}

/** Today's date in the browser's timezone, as YYYY-MM-DD. */
export function localToday(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The mapping in force today: the latest one whose effective date has arrived. */
export function currentMapping<T extends CategoryMapping>(
  mappings: T[],
  direction: Direction,
  today = localToday(),
): T | undefined {
  return mappings
    .filter((m) => m.direction === direction && m.effective_from <= today)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]
}

/** A mapping change scheduled for a future date, if any. */
export function scheduledMapping<T extends CategoryMapping>(
  mappings: T[],
  direction: Direction,
  today = localToday(),
): T | undefined {
  return mappings
    .filter((m) => m.direction === direction && m.effective_from > today)
    .sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0]
}
